import {
  Component,
  Input,
  signal,
  computed,
  ElementRef,
  HostListener,
  inject,
  forwardRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

type PickerMode = 'days' | 'months' | 'years';

@Component({
  selector: 'app-date-time-picker',
  imports: [CommonModule],
  templateUrl: './date-time-picker.html',
  styleUrl: './date-time-picker.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateTimePicker),
      multi: true
    }
  ]
})
export class DateTimePicker implements ControlValueAccessor {
  private elementRef = inject(ElementRef);

  @Input() placeholder = 'Select Date & Time';
  @Input() minDate: string | null = null;
  @Input() dropdownAlign: 'left' | 'right' = 'left';

  value = signal<Date | null>(null);
  isOpen = signal(false);
  mode = signal<PickerMode>('days');
  viewDate = signal(new Date());

  // Time signals
  selectedHour = signal(12);
  selectedMinute = signal(0);
  selectedPeriod = signal<'AM' | 'PM'>('PM');

  monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];
  weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  yearRangeStart = computed(() => {
    const year = this.viewDate().getFullYear();
    return Math.floor(year / 12) * 12;
  });

  yearOptions = computed(() => {
    const start = this.yearRangeStart();
    return Array.from({ length: 12 }, (_, i) => start + i);
  });

  hours = Array.from({ length: 12 }, (_, i) => i + 1);
  minutes = Array.from({ length: 12 }, (_, i) => i * 5);

  onChange: (value: any) => void = () => {
    /* empty */
  };

  onTouched: () => void = () => {
    /* empty */
  };

  formattedValue = computed(() => {
    const val = this.value();
    if (!val) return '';

    const d = val.getDate().toString().padStart(2, '0');
    const m = (val.getMonth() + 1).toString().padStart(2, '0');
    const y = val.getFullYear();

    let h = val.getHours();
    const period = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    h = h ? h : 12; // the hour '0' should be '12'
    const min = val.getMinutes().toString().padStart(2, '0');

    return `${d}-${m}-${y} ${h}:${min} ${period}`;
  });

  calendarDays = computed(() => {
    const date = this.viewDate();
    const year = date.getFullYear();
    const month = date.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDayOfMonth.getDay();
    const days: any[] = [];

    const prevMonthLastDay = new Date(year, month, 0);
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay.getDate() - i),
        currentMonth: false
      });
    }

    for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
      const d = new Date(year, month, i);
      days.push({
        date: d,
        currentMonth: true,
        isToday: this.isToday(d)
      });
    }

    const totalDaysSoFar = days.length;
    for (let i = 1; i <= 42 - totalDaysSoFar; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        currentMonth: false
      });
    }

    return days;
  });

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closePicker();
    }
  }

  togglePicker() {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    this.isOpen() ? this.closePicker() : this.openPicker();
  }

  openPicker() {
    this.isOpen.set(true);
    this.mode.set('days');
    const val = this.value();
    if (val) {
      this.viewDate.set(new Date(val));
      let h = val.getHours();
      this.selectedPeriod.set(h >= 12 ? 'PM' : 'AM');
      h = h % 12;
      this.selectedHour.set(h ? h : 12);
      this.selectedMinute.set(Math.floor(val.getMinutes() / 5) * 5);
    } else {
      const now = new Date();
      this.viewDate.set(now);
      let h = now.getHours();
      this.selectedPeriod.set(h >= 12 ? 'PM' : 'AM');
      h = h % 12;
      this.selectedHour.set(h ? h : 12);
      this.selectedMinute.set(Math.floor(now.getMinutes() / 5) * 5);
    }
  }

  closePicker() {
    this.isOpen.set(false);
    this.onTouched();
  }

  toggleMode() {
    if (this.mode() === 'days') this.mode.set('months');
    else if (this.mode() === 'months') this.mode.set('years');
    else this.mode.set('days');
  }

  prevPage() {
    const current = this.viewDate();
    if (this.mode() === 'days') {
      this.viewDate.set(new Date(current.getFullYear(), current.getMonth() - 1, 1));
    } else if (this.mode() === 'months') {
      this.viewDate.set(new Date(current.getFullYear() - 1, current.getMonth(), 1));
    } else {
      this.viewDate.set(new Date(current.getFullYear() - 12, current.getMonth(), 1));
    }
  }

  nextPage() {
    const current = this.viewDate();
    if (this.mode() === 'days') {
      this.viewDate.set(new Date(current.getFullYear(), current.getMonth() + 1, 1));
    } else if (this.mode() === 'months') {
      this.viewDate.set(new Date(current.getFullYear() + 1, current.getMonth(), 1));
    } else {
      this.viewDate.set(new Date(current.getFullYear() + 12, current.getMonth(), 1));
    }
  }

  selectDate(date: Date) {
    if (this.isDisabled(date)) return;
    this.updateValue(date);
  }

  updateValue(baseDate?: Date) {
    const d = baseDate || this.value() || new Date();
    const newDate = new Date(d);

    let h = this.selectedHour();
    if (this.selectedPeriod() === 'PM' && h < 12) h += 12;
    if (this.selectedPeriod() === 'AM' && h === 12) h = 0;

    newDate.setHours(h, this.selectedMinute(), 0, 0);
    this.value.set(newDate);

    // Format as local date-time string (YYYY-MM-DD HH:mm:ss)
    const year = newDate.getFullYear();
    const month = (newDate.getMonth() + 1).toString().padStart(2, '0');
    const day = newDate.getDate().toString().padStart(2, '0');
    const hours = newDate.getHours().toString().padStart(2, '0');
    const minutes = newDate.getMinutes().toString().padStart(2, '0');

    const localDateTime = `${year}-${month}-${day} ${hours}:${minutes}`;
    this.onChange(localDateTime);
  }

  selectHour(h: number) {
    this.selectedHour.set(h);
    this.updateValue();
  }

  selectMinute(m: number) {
    this.selectedMinute.set(m);
    this.updateValue();
  }

  setPeriod(p: 'AM' | 'PM') {
    this.selectedPeriod.set(p);
    this.updateValue();
  }

  selectMonth(month: number) {
    const current = this.viewDate();
    this.viewDate.set(new Date(current.getFullYear(), month, 1));
    this.mode.set('days');
  }

  selectYear(year: number) {
    const current = this.viewDate();
    this.viewDate.set(new Date(year, current.getMonth(), 1));
    this.mode.set('months');
  }

  clearValue(event?: Event) {
    if (event) event.stopPropagation();
    this.value.set(null);
    this.onChange(null);
    this.closePicker();
  }

  setNow() {
    const now = new Date();
    this.value.set(now);

    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const seconds = '00';

    const localDateTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    this.onChange(localDateTime);
    this.closePicker();
  }

  isSelected(date: Date) {
    const val = this.value();
    if (!val) return false;
    return (
      date.getDate() === val.getDate() &&
      date.getMonth() === val.getMonth() &&
      date.getFullYear() === val.getFullYear()
    );
  }

  isDisabled(date: Date) {
    if (!this.minDate) return false;
    const min = new Date(this.minDate);
    min.setHours(0, 0, 0, 0);
    const check = new Date(date);
    check.setHours(0, 0, 0, 0);
    return check.getTime() < min.getTime();
  }

  isToday(date: Date) {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }

  writeValue(value: any): void {
    if (value) {
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        this.value.set(d);
      }
    } else {
      this.value.set(null);
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
